import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { Button, Col, Form, Row, Card } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { BsArrowDown, BsArrowLeft } from "react-icons/bs";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import cpfMask from "../../../components/Formatters/cpfMask";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import SelectInput from "../../../components/UI/SelectInput/SelectInput";
import DateTimeInputFormik from "../../../components/UI/DateTimeInput/DateTimeInputFormik";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import constants from "../../Constants";

const formatarNuPat = (cdProcesso) => {
  if (!cdProcesso) return "";
  const parts = cdProcesso.split("/");
  if (parts.length !== 2) return cdProcesso;
  return `${parts[0].slice(-4)}/${parts[1]}`;
};

const statusProcessoMap = {
  P: "Aberto",
  C: "Cancelado",
  B: "Baixado",
  A: "Aguardando Checklist",
  E: "Em ação fiscal",
  D: "PDA",
};

const modoCienciaOptions = [
  { key: "0", value: "", text: "Selecione" },
  { key: "1", value: "A", text: "AR" },
  { key: "2", value: "P", text: "Pessoal" },
  { key: "3", value: "E", text: "Edital" },
  { key: "4", value: "D", text: "DTE" },
];

const validationSchema = Yup.object().shape({
  sqMomento: Yup.string().required("Momento é obrigatório"),
  tpModoCiencia: Yup.string().required("Modo de ciência é obrigatório"),
  dtCiencia: Yup.string().when("tpModoCiencia", {
    is: (val) => val && val !== "E",
    then: Yup.string().required("Data de ciência é obrigatória"),
    otherwise: Yup.string().default(""),
  }),
  dtPublicacaoEdital: Yup.string().when("tpModoCiencia", {
    is: "E",
    then: Yup.string().required("Data de publicação do edital é obrigatória"),
    otherwise: Yup.string().default(""),
  }),
  nuCpf: Yup.string().default(""),
  nmContribuinte: Yup.string().default(""),
  txCargo: Yup.string().default(""),
});

const cienciaColumns = [
  {
    name: "Momento",
    selector: (row) => row.dsMomentoAnalisado,
    sortable: true,
    width: "20%",
  },
  {
    name: "Modo Ciência",
    selector: (row) => row.dsTpModoCiencia,
    center: true,
    width: "12%",
  },
  {
    name: "Data Ciência",
    selector: (row) => row.dtCiencia,
    center: true,
    width: "12%",
  },
  {
    name: "CPF",
    selector: (row) => row.nuCpf,
    center: true,
    width: "12%",
  },
  {
    name: "Nome",
    selector: (row) => row.nmContribuinte,
    width: "18%",
  },
  {
    name: "Status",
    selector: (row) => row.dsStCiencia,
    center: true,
    width: "10%",
  },
];

const MomentoCiencia = ({ location, history }) => {
  const sqProcesso = location?.state?.sqProcesso;
  const cdProcesso = location?.state?.cdProcesso;
  const sqAcaoFiscal = location?.state?.sqAcaoFiscal;

  const [ciencias, setCiencias] = useState([]);
  const [momentoOptions, setMomentoOptions] = useState([{ key: "0", value: "", text: "Selecione" }]);
  const [dadosProcesso, setDadosProcesso] = useState(null);
  const [cpfEncontrado, setCpfEncontrado] = useState(false);
  const [cargoEncontrado, setCargoEncontrado] = useState(false);
  const [erroInclusao, setErroInclusao] = useState(null);

  const { request: requestCiencias, data: dataCiencias, isLoading: loadingCiencias } = useApiClient();
  const { request: requestMomentos, data: dataMomentos } = useApiClient();
  const { request: requestDadosProcesso, data: dataDadosProcesso } = useApiClient();
  const {
    request: requestIncluir,
    data: dataIncluir,
    errors: errorsIncluir,
    isLoading: loadingIncluir,
  } = useApiClient();
  const { request: requestCpf, data: dataCpf } = useApiClient();

  // Buscar dados do processo e momentos disponíveis ao montar
  useEffect(() => {
    if (sqProcesso) {
      requestDadosProcesso(apiRoutes.fiscalizacao.pat.consultarDadosProcesso, { SqProcesso: sqProcesso });
      requestMomentos(apiRoutes.fiscalizacao.pat.listaMomentoDisponivel, { SqProcesso: sqProcesso });
      requestCiencias(apiRoutes.fiscalizacao.pat.listaMomentoCiencia, { SqProcesso: sqProcesso });
    }
  }, [sqProcesso, requestDadosProcesso, requestMomentos, requestCiencias]);

  // Popular dados do processo
  useEffect(() => {
    if (dataDadosProcesso?.result) {
      const dados = Array.isArray(dataDadosProcesso.result) ? dataDadosProcesso.result[0] : dataDadosProcesso.result;
      setDadosProcesso(dados);
    }
  }, [dataDadosProcesso]);

  // Popular dropdown de momentos
  useEffect(() => {
    if (dataMomentos?.result) {
      const options = [
        { key: "0", value: "", text: "Selecione" },
        ...dataMomentos.result.map((item, index) => ({
          key: String(index + 1),
          value: String(item.sqMomento),
          text: item.dsMomentoAnalisado,
        })),
      ];
      setMomentoOptions(options);
    }
  }, [dataMomentos]);

  // Popular tabela de ciências
  useEffect(() => {
    if (dataCiencias?.result) {
      setCiencias(dataCiencias.result || []);
    }
  }, [dataCiencias]);

  // Extrai mensagem amigável de erros da API (remove stack trace)
  const extrairMensagemErro = (msg) => {
    if (!msg || typeof msg !== "string") return "Erro ao incluir ciência.";
    const matchErro = msg.match(/Erro:\s*(.+?)(?:\s+-\s+(?:em|at)\s+|$)/s);
    if (matchErro) return matchErro[1].trim();
    const semStack = msg.split(/\s+-\s+(?:em|at)\s+/)[0];
    return semStack.trim() || "Erro ao incluir ciência.";
  };

  // Tratar retorno da inclusão
  useEffect(() => {
    if (dataIncluir?.result && !loadingIncluir) {
      if (typeof dataIncluir.result === "string" && dataIncluir.result.includes("Lamentamos")) {
        setErroInclusao({ message: extrairMensagemErro(dataIncluir.result) });
      } else {
        setErroInclusao(null);
        toast.success("Ciência incluída com sucesso!");
        formik.resetForm();
        setCpfEncontrado(false);
        setCargoEncontrado(false);
        requestCiencias(apiRoutes.fiscalizacao.pat.listaMomentoCiencia, { SqProcesso: sqProcesso });
      }
    }
    if (errorsIncluir && !loadingIncluir) {
      const msg = errorsIncluir?.message || errorsIncluir;
      setErroInclusao({ message: extrairMensagemErro(typeof msg === "string" ? msg : "") });
    }
  }, [dataIncluir, errorsIncluir, loadingIncluir]);

  const formik = useFormik({
    initialValues: {
      sqMomento: "",
      tpModoCiencia: "",
      dtCiencia: "",
      dtPublicacaoEdital: "",
      nuCpf: "",
      nmContribuinte: "",
      txCargo: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const toDateBR = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
      const params = {
        SqProcesso: sqProcesso,
        SqMomento: values.sqMomento,
        TpModoCiencia: values.tpModoCiencia,
        DtCiencia: toDateBR(values.dtCiencia),
        DtPublicacaoEdital: toDateBR(values.dtPublicacaoEdital),
        FgDecisaoDefinitiva: "S",
        NuCpf: values.nuCpf.replace(/\D/g, ""),
        NmContribuinte: values.nmContribuinte,
        TxCargo: values.txCargo,
      };
      requestIncluir(apiRoutes.fiscalizacao.pat.adicionarMomentoCiencia, params);
    },
  });

  const isEdital = formik.values.tpModoCiencia === "E";

  // Buscar representante por CPF (onBlur)
  const buscaCpf = () => {
    const cpf = formik.values.nuCpf.replace(/\D/g, "");
    if (cpf.length === 11) {
      requestCpf(apiRoutes.fiscalizacao.ordemservico.consultaRepresentante, {
        cpfRepresentante: formik.values.nuCpf,
        sqAcaoFiscal: sqAcaoFiscal,
      });
    } else {
      setCpfEncontrado(false);
      setCargoEncontrado(false);
    }
  };

  // Tratar retorno da busca de CPF
  useEffect(() => {
    if (dataCpf) {
      const resultado = dataCpf.result?.[0];
      const nome = resultado?.nomeRepresentante || "";
      const cargo = resultado?.cargoRepresentante || "";
      formik.setValues((curr) => ({
        ...curr,
        nuCpf: cpfMask(curr.nuCpf),
        nmContribuinte: nome,
        txCargo: cargo,
      }));
      setCpfEncontrado(nome.length > 0);
      setCargoEncontrado(cargo.length > 0);
    }
  }, [dataCpf]);

  // Limpar campos condicionais quando modo ciência muda
  useEffect(() => {
    if (isEdital) {
      formik.setFieldValue("dtCiencia", "");
    } else {
      formik.setFieldValue("dtPublicacaoEdital", "");
    }
  }, [formik.values.tpModoCiencia]);

  if (!sqProcesso) {
    return (
      <div className="container-fluid">
        <h1 className="h3 mb-4 text-gray-800">Momento Ciência</h1>
        <div className="alert alert-warning">Processo não informado. Volte à consulta de PAT.</div>
      </div>
    );
  }

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">
        <Button variant="link" className="p-0 mr-2" onClick={() => history.goBack()}>
          <BsArrowLeft size={22} />
        </Button>
        Momento Ciência - PAT {cdProcesso}
      </h1>

      <div className="row">
        <div className="col-lg-12">
          {/* Cabeçalho do Processo */}
          {dadosProcesso && (
            <div className="p-4">
              <Row>
                <Col xs="6" lg="2" className="mb-2">
                  <Card.Title as="strong">Inscrição Estadual</Card.Title>
                  <Card.Text as="p">{dadosProcesso.nuInscEstadual || "-"}</Card.Text>
                </Col>
                <Col xs="6" lg="2" className="mb-2">
                  <Card.Title as="strong">{dadosProcesso.tpPessoa}</Card.Title>
                  <Card.Text as="p">{dadosProcesso.nuDocumento || "-"}</Card.Text>
                </Col>
                <Col xs="12" lg="3" className="mb-2">
                  <Card.Title as="strong">Interessado</Card.Title>
                  <Card.Text as="p">{dadosProcesso.nmFormal}</Card.Text>
                </Col>
                <Col xs="6" lg="3" className="mb-2">
                  <Card.Title as="strong">Protocolo</Card.Title>
                  <Card.Text as="p">{dadosProcesso.cdPgeProcesso || "-"}</Card.Text>
                </Col>
                <Col xs="6" lg="2" className="mb-2">
                  <Card.Title as="strong">Status</Card.Title>
                  <Card.Text as="p">
                    {statusProcessoMap[dadosProcesso.stProcesso] || dadosProcesso.stProcesso}
                  </Card.Text>
                </Col>
              </Row>
            </div>
          )}

          {/* Formulário de Inclusão */}
          <Card className="shadow mb-4">
            <Card.Header className="py-3">
              <h6 className="font-weight-bold text-primary m-0">Incluir Ciência</h6>
            </Card.Header>
            <Card.Body>
              <FormAlert formik={formik} errors={erroInclusao} isLoading={loadingIncluir} dismissible />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <Row>
                  <Form.Group as={Col} sm={12} md={4}>
                    <Form.Label>Momento</Form.Label>
                    <SelectInput formik={formik} field="sqMomento" options={momentoOptions} />
                  </Form.Group>
                  <Form.Group as={Col} sm={12} md={4}>
                    <Form.Label>Modo de Ciência</Form.Label>
                    <SelectInput formik={formik} field="tpModoCiencia" options={modoCienciaOptions} />
                  </Form.Group>
                  {isEdital ? (
                    <Form.Group as={Col} sm={12} md={4}>
                      <Form.Label>Data Publicação Edital</Form.Label>
                      <DateTimeInputFormik formik={formik} field="dtPublicacaoEdital" />
                    </Form.Group>
                  ) : (
                    <Form.Group as={Col} sm={12} md={4}>
                      <Form.Label>Data de Ciência</Form.Label>
                      <DateTimeInputFormik formik={formik} field="dtCiencia" />
                    </Form.Group>
                  )}
                </Row>
                <Row className="mt-2">
                  <Form.Group as={Col} sm={12} md={4}>
                    <Form.Label>CPF</Form.Label>
                    <TextInputFormik
                      placeholder="Informe o CPF"
                      formik={formik}
                      field="nuCpf"
                      maxLength="14"
                      evento={buscaCpf}
                    />
                  </Form.Group>
                  <Form.Group as={Col} sm={12} md={4}>
                    <Form.Label>Nome do Contribuinte</Form.Label>
                    <TextInputFormik
                      placeholder="Informe o nome"
                      formik={formik}
                      field="nmContribuinte"
                      disabled={cpfEncontrado}
                    />
                  </Form.Group>
                  <Form.Group as={Col} sm={12} md={4}>
                    <Form.Label>Cargo</Form.Label>
                    <TextInputFormik
                      placeholder="Informe o cargo"
                      formik={formik}
                      field="txCargo"
                      disabled={cargoEncontrado}
                    />
                  </Form.Group>
                </Row>
                <Row className="mt-3">
                  <Form.Group as={Row} style={{ marginLeft: 0 }} className="d-flex flex-row">
                    <Col sm="12">
                      <Button className="btn btn-primary btn-md mr-2" type="submit" disabled={loadingIncluir}>
                        Incluir
                      </Button>
                      <Button
                        className="btn btn-primary btn-md mr-2"
                        variant="light"
                        onClick={() => {
                          formik.resetForm();
                          setCpfEncontrado(false);
                          setCargoEncontrado(false);
                        }}
                      >
                        Limpar
                      </Button>
                    </Col>
                  </Form.Group>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          {/* Tabela de Ciências Existentes */}
          <Card className="shadow mb-4">
            <Card.Header className="py-3">
              <h6 className="font-weight-bold text-primary m-0">Ciências Registradas</h6>
            </Card.Header>
            <Card.Body>
              <DataTable
                noHeader
                keyField="sqMomentoCiencia"
                columns={cienciaColumns}
                data={ciencias}
                progressPending={loadingCiencias}
                noDataComponent="Nenhuma ciência registrada"
                progressComponent={<TextSpinner />}
                sortable
                sortIcon={<BsArrowDown />}
                pagination
                paginationComponentOptions={constants.paginationComponentOptions}
              />
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
};

export default MomentoCiencia;
